<script lang="ts">
	import { createEventDispatcher, onMount } from 'svelte';
	import { Button, Modal, Label, Input, Textarea, Spinner } from 'flowbite-svelte';
	import { PaperClip } from 'svelte-heros-v2';

	import PhoneInput from '../ui/PhoneInput/PhoneInput.svelte';
	import RegionSelect from '$lib/components/data_components/RegionSelect.svelte';
	import TopicSelect from '$lib/components/data_components/TopicSelect.svelte';
	import getFormFileds from '$lib/helpers/getFormFields';
	import { FeedbackApi } from '$lib/services/feedback';
	import { addToast } from '$lib/stores/toasts';
	import { deviceWidth } from '$lib/stores';

	const dispatch = createEventDispatcher();

	export let show = false;

	let files: any[] = [];
	let form;
	let loading = false;

	function onFileChange(e) {
		if (e.target.files?.length) {
			files = e.target.files;
		} else {
			files = [];
		}
	}

	const clear = () => {
		files = [];
		form?.reset();
	};

	const submitForm = async (e) => {
		const formData = new FormData(e.target);
		const valuesFormData = getFormFileds(formData);

		loading = true;

		const { err, data } = await FeedbackApi().postFeedback(valuesFormData);

		if (err) {
			addToast({ title: 'Возникла ошибка!', message: 'Не удалось отправить форму', type: 'error' });
			loading = false;
		}

		if (data?.id) {
			addToast({ title: 'Сообщение успешно отправлено!', type: 'success' });
			loading = false;
			close();
		}
	};

	const close = () => {
		files = [];
		dispatch('close');
	};

	onMount(() => {
		return () => clear();
	});

	const getModalSize = (width): 'xs' | 'sm' | 'md' | 'lg' | 'xl' => {
		if (width >= 1920) {
			return 'xl';
		}

		if (width >= 1440) {
			return 'lg';
		}

		if (width >= 1366) {
			return 'md';
		}

		return 'lg';
	};

	const labelClass =
		'mb-[9px] 2xl:mb-[6px] 3xl:mb-[6px] 5xl:mb-[7px] 6xl:mb-[9px] text-[14px] 2xl:text-[9.96px] 3xl:text-[10.5px] 5xl:text-[11.667px] 6xl:text-[14px] leading-[1.2]';
	const inputClass =
		'px-[20px] 2xl:px-[14px] 3xl:px-[13px] 5xl:px-[16.5px] 5xl:px-[18.5px] 2xl:py-[3.5px] 3xl:py-[4.5px] 5xl:py-[6.5px] 6xl:py-[10px] rounded-[10px] 2xl:rounded-[7.115px] 3xl:rounded-[7.5px] 5xl:rounded-[8.333px] 6xl:rounded-[10px] 2xl:text-[9.96px] 3xl:text-[10.5px] 5xl:text-[11.667px] 6xl:text-[14px] bg-white';
</script>

<Modal
	bind:open={show}
	size={getModalSize($deviceWidth)}
	on:close={close}
	autoclose={false}
	outsideclose
	defaultClass="relative flex flex-col mx-auto rounded-[14px] 6xl:rounded-[20px] pt-2 2xl:pt-3 5xl:pt-4 6xl:pt-6 pb-4 2xl:pb-5 5xl:pb-7 6xl:pb-9 2xl:px-4 3xl:px-5 5xl:px-7 6xl:px-9"
>
	<div
		class="text-center text-neutral-800 text-[15px] 2xl:text-[14px] 3xl:text-[15px] 5xl:text-[16.667px] 6xl:text-[20px] leading-[1.5] font-normal px-[10%] 2xl:px-[0]"
	>
		Заполните контактные данные для связи с Вами и опишите суть Вашего обращения.
	</div>
	<form bind:this={form} action="" method="POST" on:submit|preventDefault={submitForm}>
		<div
			class="grid grid-cols-1 2xl:grid-cols-2 gap-[14px] 2xl:gap-x-[28px] 3xl:gap-x-[30px] 5xl:gap-x-[33px] 6xl:gap-x-[40px] 2xl:gap-y-[10px] 3xl:gap-y-[12px] 5xl:gap-y-[12.5px] 6xl:gap-y-[14px]"
		>
			<div>
				<Label for="feedback-modal-form-fio-field" class={labelClass}>ФИО</Label>
				<Input
					type="text"
					name="name"
					id="feedback-modal-form-fio-field"
					placeholder="Введите ваше имя"
					class={inputClass}
				/>
			</div>
			<div class="2xl:col-start-2 2xl:row-span-5 order-6 2xl:order-none">
				<Label for="feedback-modal-form-message-field" class="{labelClass} shrink-0">
					Обращение
				</Label>
				<Textarea
					id="feedback-modal-form-message-field"
					name="message"
					placeholder="Введите обращение"
					class="{inputClass} h-[180px] 2xl:h-[205px] 3xl:h-[216px] 5xl:h-[240px] 6xl:h-[288px]"
				/>
				<div class="mt-5 2xl:mt-[22px] 3xl:mt-[24px] 5xl:mt-[28px] 6xl:mt-[34px]">
					<div
						class="grid grid-cols-[auto_auto] 2xl:grid-cols-[1fr_22px_auto] 5xl:grid-cols-[1fr_24px_auto] 6xl:grid-cols-[1fr_30px_auto] items-center justify-between gap-[18px] 5xl:gap-[20px] 6xl:gap-[30px]"
					>
						<div class="overflow-hidden col-span-2 2xl:col-auto">
							{#each files as file}
								<div
									class="w-full whitespace-nowrap overflow-hidden text-ellipsis text-[16px] 2xl:text-[11.383px] 3xl:text-[12px] 5xl:text-[13.333px] 6xl:text-[16px]"
								>
									{file?.name}
								</div>
							{/each}
						</div>
						<label class="cursor-pointer shrink-0">
							<input type="file" name="file" class="hidden" on:change={onFileChange} />
							<PaperClip class="outline-none text-zinc-300 w-[28px] h-[28px]" />
						</label>
						<div class="text-right">
							<Button
								type="submit"
								class="bg-blue-400 hover:bg-blue-400 focus:ring-0 !p-0 text-[16px] 2xl:text-[11.383px] 3xl:text-[12px] 5xl:text-[13.333px] 6xl:text-[16px] w-[220px] 2xl:w-[164px] 3xl:w-[172px] 5xl:w-[192px] 6xl:w-[230px] h-[42px] 2xl:h-[29.881px] 3xl:h-[31.5px] 5xl:h-[35px] 6xl:h-[42px] rounded-[10px] 2xl:rounded-[7.115px] 3xl:rounded-[7.5px] 5xl:rounded-[8.333px] 6xl:rounded-[10px]"
								disabled={loading}
							>
								{#if loading}
									<Spinner size="6" class="mt-[1px]" color="white" />
								{:else}
									Отправить
								{/if}
							</Button>
						</div>
					</div>
				</div>
			</div>
			<div>
				<Label for="feedback-modal-form-phone-field" class={labelClass}>Телефон</Label>
				<PhoneInput
					name="phone"
					id="feedback-modal-form-phone-field"
					placeholder="Введите ваш телефон"
					class={inputClass}
				/>
			</div>
			<div>
				<Label for="feedback-modal-form-mail-field" class={labelClass}>Почта</Label>
				<Input
					type="text"
					name="email"
					id="feedback-modal-form-mail-field"
					placeholder="Введите ваш e-mail"
					class={inputClass}
				/>
			</div>
			<div>
				<Label for="feedback-modal-form-region-field" class={labelClass}>Субъект</Label>
				<RegionSelect
					name="region"
					id="feedback-modal-form-region-field"
					placeholder="Выберите субъект"
					class={inputClass}
				/>
			</div>
			<div>
				<Label for="feedback-modal-form-topic-field" class={labelClass}>Тема обращения</Label>
				<TopicSelect
					name="topicAppeal"
					id="feedback-modal-form-topic-field"
					placeholder="Выберите тему"
					class={inputClass}
				/>
			</div>
		</div>
	</form>
</Modal>
